// 화면 19 — 시장 전체 밴드 분포 누적 막대 (P0).
// 권위: ARCH_DASHBOARD §4.19 / §6.2 — GET /api/v1/stocks/score-distribution/bands?days=N.
// N 토글 {30, 60, 90}, default 60 (QUANT v2.4.0 §2.5.6).
//
// SVG stacked column chart — 일자별 4 segment (A / B / C / unclassified) 누적.
// 색상은 Phase B 와 일관 (A=mark-up, B=warn, C=mark-down, unclassified=ink-faint).

import { memo, useMemo } from 'react';
import type { BandDistributionRow } from './types';

interface Props {
  rows: BandDistributionRow[];
}

const VIEW_W = 960;
const VIEW_H = 260;
const PAD = { top: 12, right: 16, bottom: 36, left: 56 };

type Segment = 'A' | 'B' | 'C' | 'unclassified';

const SEGMENTS: readonly { key: Segment; field: keyof BandDistributionRow; color: string; label: string }[] = [
  { key: 'A', field: 'A_count', color: 'var(--mark-up)', label: 'A (강세)' },
  { key: 'B', field: 'B_count', color: 'var(--warn)', label: 'B (중립)' },
  { key: 'C', field: 'C_count', color: 'var(--mark-down)', label: 'C (약세)' },
  {
    key: 'unclassified',
    field: 'unclassified_count',
    color: 'var(--ink-faint)',
    label: 'unclassified',
  },
];

export const BandDistributionChart = memo(function BandDistributionChart({
  rows,
}: Props): JSX.Element {
  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.trading_date.localeCompare(b.trading_date)),
    [rows]
  );

  const totals = useMemo(
    () =>
      sorted.map(
        (r) => r.A_count + r.B_count + r.C_count + r.unclassified_count
      ),
    [sorted]
  );

  const yMax = useMemo(() => {
    const m = totals.reduce((acc, v) => (v > acc ? v : acc), 0);
    return m === 0 ? 1 : m;
  }, [totals]);

  const plotW = VIEW_W - PAD.left - PAD.right;
  const plotH = VIEW_H - PAD.top - PAD.bottom;

  const colWidth = sorted.length > 0 ? plotW / sorted.length : plotW;
  // 막대 간격 — 좌우 1px 여백 (조밀한 60~90 일자 가독성).
  const barWidth = Math.max(colWidth - 1, 1);

  const xOf = (i: number): number => PAD.left + i * colWidth + colWidth / 2;
  const yOf = (v: number): number => PAD.top + (1 - v / yMax) * plotH;

  const yTicks = useMemo(() => {
    const n = 4;
    return Array.from({ length: n + 1 }, (_, i) => (yMax * i) / n);
  }, [yMax]);

  const xTickIdx = useMemo(() => {
    if (sorted.length === 0) return [] as number[];
    if (sorted.length === 1) return [0];
    const k = 6;
    const arr = new Set<number>([0, sorted.length - 1]);
    for (let i = 1; i < k - 1; i += 1) {
      arr.add(Math.round((i * (sorted.length - 1)) / (k - 1)));
    }
    return [...arr].sort((a, b) => a - b);
  }, [sorted.length]);

  return (
    <svg
      className="sse-band-dist-chart"
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="시장 전체 밴드 분포 누적 막대"
    >
      {yTicks.map((t) => {
        const y = yOf(t);
        return (
          <line
            key={`grid-${t}`}
            className="sse-band-dist-chart__grid"
            x1={PAD.left}
            x2={VIEW_W - PAD.right}
            y1={y}
            y2={y}
          />
        );
      })}
      {yTicks.map((t) => (
        <text
          key={`yt-${t}`}
          className="sse-band-dist-chart__tick"
          x={PAD.left - 6}
          y={yOf(t) + 3}
          textAnchor="end"
        >
          {Math.round(t)}
        </text>
      ))}
      {xTickIdx.map((i) => (
        <text
          key={`xt-${i}`}
          className="sse-band-dist-chart__tick"
          x={xOf(i)}
          y={VIEW_H - PAD.bottom + 14}
          textAnchor="middle"
        >
          {sorted[i]?.trading_date.slice(5)}
        </text>
      ))}

      <line
        className="sse-band-dist-chart__axis"
        x1={PAD.left}
        x2={VIEW_W - PAD.right}
        y1={PAD.top + plotH}
        y2={PAD.top + plotH}
      />
      <line
        className="sse-band-dist-chart__axis"
        x1={PAD.left}
        x2={PAD.left}
        y1={PAD.top}
        y2={PAD.top + plotH}
      />

      {sorted.map((r, i) => {
        let cum = 0;
        const cx = xOf(i);
        const segments = SEGMENTS.map((seg) => {
          const val = (r[seg.field] as number) ?? 0;
          const y0 = yOf(cum);
          const y1 = yOf(cum + val);
          cum += val;
          const segH = Math.max(y0 - y1, 0);
          return (
            <rect
              key={`${r.trading_date}-${seg.key}`}
              x={cx - barWidth / 2}
              y={y1}
              width={barWidth}
              height={segH}
              fill={seg.color}
              opacity={seg.key === 'unclassified' ? 0.55 : 0.85}
            >
              <title>
                {r.trading_date} · {seg.label} · {val} (
                {totals[i] > 0 ? ((val * 100) / totals[i]).toFixed(1) : '0.0'}%)
              </title>
            </rect>
          );
        });
        return <g key={`col-${r.trading_date}`}>{segments}</g>;
      })}
    </svg>
  );
});

export function BandDistributionLegend(): JSX.Element {
  return (
    <div className="sse-band-dist-chart__legend" aria-label="범례">
      {SEGMENTS.map((s) => (
        <span key={s.key} className="sse-band-dist-chart__legend-item">
          <span
            className="sse-band-dist-chart__legend-swatch"
            style={{
              background: s.color,
              opacity: s.key === 'unclassified' ? 0.55 : 0.85,
            }}
          />
          {s.label}
        </span>
      ))}
    </div>
  );
}
